import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { getCountryFromCode } from '@cineamo/ui/src/helpers/international-helper';
import { ISO_3166_1_LOCALE, ISO_639_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';

import { Locale } from '../constants/locale-constants';
import { TMDB_IMAGE_RESOLUTION, VideoSites } from '../constants/tmdb-constants';
import { VIMEO_THUMBNAIL_IMAGE_DEFAULT, VIMEO_VIDEO_URL } from '../constants/vimeo-constants';
import {
    getYoutubeVideoId,
    YOUTUBE_THUMBNAIL_IMAGE_MAX_RES,
    YOUTUBE_EMBED_VIDEO_URL
} from '../constants/youtube-constants';
import { CrewDto } from '../models/movie/CrewDto.types';
import { ImageDto } from '../models/movie/ImagesDto.types';
import { MovieAccessibilityDto } from '../models/movie/MovieAccessibilityDto.types';
import { ReleaseDateType } from '../models/movie/ReleaseDateType.types';
import { Status } from '../models/movie/Status.enum';
import { TranslationDataDto } from '../models/movie/TranslationDataDto.types';
import { TranslationDto } from '../models/movie/TranslationDto.types';
import { MovieDto as MovieV1Dto } from '../models/movie/v1/MovieDto.types';
import { MovieDto as MovieV2Dto } from '../models/movie/v2/MovieDto.types';
import { VideoDto } from '../models/movie/VideoDto.types';
import { VideoType } from '../models/movie/VideoType.types';
import { DEFAULT_COUNTRY_CODE_BY_LANGUAGE as DEFAULT_COUNTRY_CODE_BY_SUPPORTED_LANGUAGE } from '../types/AppLocale';

export type MovieVersionDto = MovieV1Dto | MovieV2Dto;

// The selectors below take the movie as `MovieVersionDto | null | undefined` on purpose: every
// caller feeds them a fetch result, and our api clients spell "no movie" as `null` (a failed or
// empty GET) while TanStack Query spells the not-yet-loaded state as `undefined`. Both mean the
// same thing here, and each selector already guards with `movie?.`/`if (!movie)`.

const MoviePosterPlaceholder = loadAsset('/images/placeholders/portrait/im-posterFilmPortrait.jpg');
export const MovieBackdropPlaceholder = loadAsset('/images/placeholders/landscape/im-posterFilmLandscape.jpg');

export type VideoDtoEnhanced = VideoDto & {
    fullUrl: string;
    thumbnailUrl: string;
};

export type MovieImageOptions = {
    useTmdbUrlPrefix?: boolean;
    resolution?: TMDB_IMAGE_RESOLUTION;
    includeImagesWithoutLocale?: boolean;
    placeholder?: string;
};

// `language`/`countryCode` are optional: callers routinely pass a locale they could not resolve,
// and every selector below is written for that ("language ? filter : []"), falling back to the
// original-language or placeholder image instead of narrowing by locale.
export type MovieImageFilterOptions = {
    language?: ISO_639_1_LOCALE;
    countryCode?: ISO_3166_1_LOCALE;
    useBackdropAsFallback?: boolean;
    usePosterAsFallback?: boolean;
    usePlaceholder?: boolean;
    imageOptions?: MovieImageOptions;
};

export type MovieVideoFilterOptions = {
    language?: ISO_639_1_LOCALE;
    countryCode?: ISO_3166_1_LOCALE;
    seekToSeconds?: number;
};

export function isMovieV2(movie: MovieVersionDto): movie is MovieV2Dto {
    return 'locale' in movie;
}
export function isMovieV1(movie: MovieVersionDto): movie is MovieV1Dto {
    return 'video' in movie;
}

const matchLocale = (
    dto: { iso6391?: ISO_639_1_LOCALE | null; iso31661?: ISO_3166_1_LOCALE | null },
    options: {
        // An unresolved locale is passed as `undefined` by some callers and spelled `null` by
        // others; both mean "do not narrow by it" and the comparisons below treat them alike.
        language?: ISO_639_1_LOCALE | null;
        countryCode?: ISO_3166_1_LOCALE | null;
        includeDtoWithoutLocale?: boolean;
        includeDtoWithoutCountry?: boolean;
    }
): boolean => {
    const { language, countryCode, includeDtoWithoutLocale = false, includeDtoWithoutCountry = false } = options;
    const isLanguageMatch = dto.iso6391 === language;
    const isDtoWithoutLocale = !dto.iso6391 || (includeDtoWithoutCountry && !dto.iso31661);
    if (includeDtoWithoutLocale && isDtoWithoutLocale) {
        return isLanguageMatch;
    }
    if (!countryCode) {
        return isLanguageMatch;
    }
    const isCountryMatch = dto.iso31661 === countryCode;
    return isLanguageMatch && isCountryMatch;
};

const normalizeTitle = (title?: string): string => title?.trim().toLowerCase() ?? '';

/**
 * Images (posters, logos) of *other* locales that carry the very same title text as the viewer's
 * locale. Posters and logos are pictures of the title, so a foreign one is only usable when both
 * translations read the same. Example (viewer language `de`):
 *
 * ```
 * [{ locale: 'de', title: 'Hallo Welt',    image: null },
 *  { locale: 'fr', title: 'Bonjour Monde', image: '/fr.png' },   // different words → not usable
 *  { locale: 'en', title: 'Hallo Welt',    image: '/en.png' }]   // same words → usable for `de`
 * ```
 *
 * The original language gets no special treatment here: its image qualifies only when its title
 * happens to match the displayed one — not just because it is the original. (A viewer whose
 * language *is* the original language is already served by the callers' locale buckets.)
 *
 * Both V1 and V2 movies expose `translations.translations`.
 */
function getImagesForSameTitle(
    movie: MovieVersionDto | null | undefined,
    images: ImageDto[],
    language: ISO_639_1_LOCALE,
    countryCode?: ISO_3166_1_LOCALE
): ImageDto[] {
    const targetTitle = normalizeTitle(getMovieTitle(movie, language, countryCode));
    if (!targetTitle) return [];

    const translations = movie?.translations?.translations ?? [];
    const titleOf = (translation: TranslationDto): string =>
        // a translation of the original language without its own title falls back to the original
        // title — same resolution getMovieTitle() applies
        normalizeTitle(
            translation.data?.title ||
                (translation.iso6391 === movie?.originalLanguage ? movie?.originalTitle : undefined)
        );

    const languagesWithSameTitle = new Set<string>();
    const languagesWithOtherTitle = new Set<string>();
    const localesWithSameTitle = new Set<string>();
    for (const translation of translations) {
        const translationTitle = titleOf(translation);
        if (!translation.iso6391 || translation.iso6391 === language || !translationTitle) continue;
        if (translationTitle === targetTitle) {
            languagesWithSameTitle.add(translation.iso6391);
            localesWithSameTitle.add(`${translation.iso6391}-${translation.iso31661 ?? ''}`);
        } else {
            languagesWithOtherTitle.add(translation.iso6391);
        }
    }

    // keeps the caller's vote-average order instead of the translation order
    return images.filter((image) => {
        if (!image.iso6391) return false;
        if (localesWithSameTitle.has(`${image.iso6391}-${image.iso31661 ?? ''}`)) return true;
        // An image whose country has no matching translation (or none at all) can't be pinned to a
        // single title, so only use it when the whole language agrees on the title — otherwise an
        // `en` image could well be the "Hello World" one of a diverging en-GB/en-US translation.
        return languagesWithSameTitle.has(image.iso6391) && !languagesWithOtherTitle.has(image.iso6391);
    });
}

export function getMovieEnhancedVideos(
    movie?: MovieVersionDto | null,
    options?: MovieVideoFilterOptions
): VideoDtoEnhanced[] {
    if (!movie?.videos?.results) return [];

    // Map videos to include full URL and thumbnail URL
    let videos: VideoDtoEnhanced[] = movie.videos.results.flatMap((video) => {
        return {
            ...video,
            fullUrl:
                video.site === VideoSites.Vimeo
                    ? VIMEO_VIDEO_URL(video.key!, options?.seekToSeconds ?? 0)
                    : YOUTUBE_EMBED_VIDEO_URL(video.key!, options?.seekToSeconds ?? 0),
            thumbnailUrl:
                video.site === VideoSites.Vimeo
                    ? VIMEO_THUMBNAIL_IMAGE_DEFAULT(video.key!)
                    : YOUTUBE_THUMBNAIL_IMAGE_MAX_RES(video.key!)
        };
    });

    // Filter videos based on language and country
    if (options?.language) {
        const videosForCountry = videos.filter((video) =>
            matchLocale(video, {
                language: options.language,
                countryCode: options.countryCode
            })
        );
        const videosForLanguage = videos.filter((video) =>
            matchLocale(video, {
                language: options.language,
                countryCode: null
            })
        );
        // prefer videos for country, but if language matches it's also ok
        videos = [...videosForCountry, ...videosForLanguage];
    }

    // The country/language concat above can list the same video twice (a video matching both the
    // country and the language filter). De-duplicate by id, keeping the first (country-preferred) match.
    const uniqueVideos = videos.filter((video, index) => videos.findIndex((v) => v.id === video.id) === index);

    // Sort videos, ensuring trailers come first
    return uniqueVideos.sort((a, b) => {
        if (a.type === VideoType.Trailer && b.type !== VideoType.Trailer) return -1;
        if (a.type !== VideoType.Trailer && b.type === VideoType.Trailer) return 1;
        return 0;
    });
}

export function getMovieTrailer(
    movie?: MovieVersionDto | null,
    options?: MovieVideoFilterOptions
): VideoDtoEnhanced | undefined {
    if (movie?.preferedTrailerUrl) {
        const parsed = parseVideoUrl(movie.preferedTrailerUrl);
        return {
            iso6391: null,
            iso31661: null,
            key: parsed.key,
            site: parsed.site,
            type: VideoType.Trailer,
            fullUrl: movie.preferedTrailerUrl,
            thumbnailUrl: parsed.thumbnailUrl
        };
    }
    const trailerVideos: VideoDtoEnhanced[] = getMovieEnhancedVideos(movie, options);
    return trailerVideos.find((video) => video.type === VideoType.Trailer) || trailerVideos[0];
}

function parseVideoUrl(url: string): { key: string; site: string; thumbnailUrl: string } {
    const youtubeVideoId = getYoutubeVideoId(url);
    if (youtubeVideoId) {
        return {
            key: youtubeVideoId,
            site: VideoSites.YouTube,
            thumbnailUrl: YOUTUBE_THUMBNAIL_IMAGE_MAX_RES(youtubeVideoId)
        };
    }

    // Vimeo: various URL formats
    const vimeoPatterns = [/vimeo\.com\/video\/(\d+)/, /vimeo\.com\/(\d+)/, /player\.vimeo\.com\/video\/(\d+)/];
    for (const pattern of vimeoPatterns) {
        const match = url.match(pattern);
        if (match?.[1]) {
            return {
                key: match[1],
                site: VideoSites.Vimeo,
                thumbnailUrl: VIMEO_THUMBNAIL_IMAGE_DEFAULT(match[1])
            };
        }
    }

    return { key: '', site: '', thumbnailUrl: '' };
}

export function movieSlugOrId(movie?: MovieVersionDto | null): number | string | undefined {
    return movie?.slug || movie?.id;
}

/**
 * Resolves the movie id to use for showtimes/showings lookups.
 *
 * Kept structural so callers don't need a complete MovieDto — a full MovieVersionDto or a
 * derived CachedMovieData both satisfy it.
 */
export function getShowingsMovieId(movie?: { id?: MovieV2Dto['id'] } | null): MovieV2Dto['id'] {
    return movie?.id;
}

/**
 * Builds the `getShowings` movie filter from a numeric (`Movie.id`) or legacy (`cineamoId`) movie id.
 *
 * Only the embedded showtimes-finder widget can still supply the legacy form: its `?movie=` param
 * comes from third-party sites we do not control, so old `…m` values keep arriving and must keep
 * working. Everything else passes a numeric id and takes the `movieId` branch (CIN-13072).
 */
export function buildShowingsMovieFilter(
    movieId: MovieV2Dto['id'] | MovieV2Dto['cineamoId']
): { movieId: MovieV2Dto['id'] } | { cineamoMovieId: MovieV2Dto['cineamoId'] } {
    // The non-numeric branch is only reached for a legacy cineamoId (`…m`), so the runtime
    // String() result is a `${number}m` — narrow it back for the cineamoMovieId query slot.
    return /^\d+$/.test(String(movieId))
        ? { movieId: Number(movieId) }
        : { cineamoMovieId: String(movieId) as MovieV2Dto['cineamoId'] };
}

export function getCrewMembersByJob(movie: MovieVersionDto | null | undefined, job: string): CrewDto[] {
    return (
        movie?.credits?.crew?.filter(
            (member) => !!member.job && job.toLowerCase().includes(member.job.toLowerCase())
        ) || []
    );
}

export function getRandomMovieSearchQuery(): string {
    const currentMonth = new Date().getMonth() + 1; // +1 because otherwise it's the index starting by 0=January
    const monthQueries: Record<string, string[]> = {
        '2': ['valentinstag große gatsby crazy stupid love', 'footloose burlesque crazy rich to all the boys'],
        '3,4,5': [
            'ferris macht blau 10 dinge die ich an dir hasse drachenzähmen leicht  gemacht',
            'footloose burlesque spider-man a new universe to all the boys',
            'frühling fack ju göthe das wandelnde schloss die wilden hühner',
            'toy story',
            'stolz und vorurteil notting hill high ferris macht blau'
        ],
        '4': [
            'Ostern Ben Hur',
            'Ostern Peter Hase',
            'Ostern Zoomania Peter Hase Die Hüter des Lichts Das Leben des Brian',
            'Ostern Die Häschenschule Die Zehn Gebote'
        ],
        '6,7,8': [
            'ich weiß was du letzten sommer getan hast midsommar booksmart',
            '10 dinge die ich an dir hasse midsommar booksmart',
            'booksmart top gun guardians of the galaxy'
        ],
        '9,10': [
            'club der toten dichter harry und sally the batman',
            'harry potter und der',
            'narnia',
            'good will hunting sleepy hollow'
        ],
        '10': [
            'Halloween Nightmare Before Christmas Trick ’r Treat',
            'Halloween Nightmare Before Christmas Poltergeist Kills',
            'Halloween Scary stories to tell in the Dark The Witch Wir',
            'Halloween It Es Mother The Witch Jigsaw The Babysitter Don t Breathe'
        ],
        '10,11': [
            'Halloween hereditary elm',
            'halloween midsommar insidious',
            'knives out shutter island American psycho'
        ],
        '1,11,12': [
            'Grinch',
            'Kevin allein zu Haus',
            'harry potter und der',
            'Christmas Weihnachten',
            'Krampus',
            'Weihnachten Christmas Noelle Der Polarexpress Nussknacker Klaus',
            'sissi nussknacker polarexpress'
        ],
        default: [
            'The Batman',
            'Der Herr der Ringe',
            'Peter Pan',
            'Marvel Avengers Thor Iron Man',
            'Marvel Avengers Guardians of the Galaxy',
            'Deadpool wolverine spider-man',
            'The Batman Departed Unter Feinden Herr der Ringe Fluch der Karibik'
        ]
    };

    const randomMonthQueries = Object.keys(monthQueries)
        .flatMap((key) =>
            key.split(',').some((month) => month === currentMonth.toString()) ? monthQueries[key] : undefined
        )
        .filter((it) => it !== undefined);

    const query = randomMonthQueries.length > 0 ? randomMonthQueries : monthQueries['default'];
    const index = Math.round(Math.random() * (query.length - 1));

    return query[index] || '';
}

// `usePlaceholder` defaults to true, so unless a caller opts out there is always an image to
// return. The overloads say so, which keeps the (very common) `<Image src={getMoviePosterImage(…)}>`
// call sites honest without every one of them re-asserting a fallback.
export function getMoviePosterImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions & { usePlaceholder?: true }
): string;
export function getMoviePosterImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined;
export function getMoviePosterImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder = true, imageOptions } = options || {};

    const moviePosterImages = getMoviePosterImages(movie, {
        language,
        countryCode,
        useBackdropAsFallback,
        imageOptions
    });

    if (moviePosterImages.length <= 0 && usePlaceholder) {
        return imageOptions?.placeholder || MoviePosterPlaceholder;
    }
    return moviePosterImages?.[0];
}

// Loosely-typed view of the shared map so the lenient ISO_639_1_LOCALE indexing below keeps compiling.
const DEFAULT_COUNTRY_CODE_BY_LANGUAGE: Partial<Record<ISO_639_1_LOCALE, ISO_3166_1_LOCALE>> =
    DEFAULT_COUNTRY_CODE_BY_SUPPORTED_LANGUAGE;

/**
 * Whether the movie-level kill switch (CIN-14124) suppresses this image type.
 *
 * Only used to stop one type standing in for the other: the API already strips the
 * hidden type's path and list, so the normal lookups find nothing on their own. The
 * flags ride on V2 only — V1 consumes them server-side and never sends them — so a
 * V1 DTO reports false here and relies on the empty list alone, which is why the
 * cross-type fallbacks are the only place this is needed.
 */
function isImageTypeHidden(movie: MovieVersionDto | null | undefined, type: 'poster' | 'backdrop'): boolean {
    if (!movie || !isMovieV2(movie)) {
        return false;
    }
    return (type === 'poster' ? movie.isPosterHidden : movie.isBackdropHidden) === true;
}

export function getMoviePosterImages(movie?: MovieVersionDto | null, options?: MovieImageFilterOptions): string[] {
    const { language, countryCode, useBackdropAsFallback = false, imageOptions } = options || {};

    // Cinema customization: full CDN URL takes priority over TMDB images
    if (movie?.posterPath?.startsWith('http')) {
        return [movie.posterPath];
    }

    // slice(): never sort the (React Query cached) DTO array in place
    let translatedImages: ImageDto[] = (movie?.images?.posters ?? []).slice();

    translatedImages.sort(
        (imageA, imageB) =>
            // Admin-chosen primaries (CIN-14159) beat the rating (highest voteAverage first)
            Number(imageB.isPrimary ?? false) - Number(imageA.isPrimary ?? false) ||
            (imageB.voteAverage ?? 0.0) - (imageA.voteAverage ?? 0.0)
    );

    // strict match for both language and country
    const translatedImagesForCountry = language
        ? translatedImages.filter((image) => matchLocale(image, { language, countryCode }))
        : [];

    // for countries like AT or CH if no country-specific image is found it's ok to use the image for DE
    const translatedImagesForLanguage = language
        ? translatedImages.filter((image) =>
              matchLocale(image, {
                  language,
                  countryCode: DEFAULT_COUNTRY_CODE_BY_LANGUAGE[language],
                  includeDtoWithoutCountry: true,
                  includeDtoWithoutLocale: true
              })
          )
        : [];

    // Posters carry the title as text, so before reaching for a foreign-language one prefer a
    // locale that spells the title exactly like the viewer's does (see getImagesForSameTitle).
    const translatedImagesForSameTitle =
        language && translatedImagesForCountry.length === 0 && translatedImagesForLanguage.length === 0
            ? getImagesForSameTitle(movie, translatedImages, language, countryCode)
            : [];

    // Last resort, unlike logos: a poster in the original language still shows the artwork the
    // movie is recognized by, which beats falling through to the generic placeholder — and beats
    // an untagged poster, whose language is unknown rather than absent.
    const translatedImagesForOriginalLanguage = movie?.originalLanguage
        ? translatedImages.filter((image) =>
              matchLocale(image, {
                  language: movie.originalLanguage as ISO_639_1_LOCALE,
                  countryCode: null
              })
          )
        : [];

    const nonTranslatedImages = translatedImages.filter((image) => !image.iso6391);

    translatedImages = [
        ...translatedImagesForCountry,
        ...translatedImagesForLanguage,
        ...translatedImagesForSameTitle,
        ...translatedImagesForOriginalLanguage,
        ...(imageOptions?.includeImagesWithoutLocale ? nonTranslatedImages : [])
    ];

    let translatedImageUrls: string[] = translatedImages
        .map((image) => image.filePath ?? '')
        .filter((url) => url.length > 0)
        // the locale buckets can overlap (e.g. a same-title poster that is also the original
        // language's), so de-duplicate — galleries would otherwise show a poster twice
        .filter((url, index, urls) => urls.indexOf(url) === index);

    if (movie && translatedImageUrls.length <= 0 && movie.posterPath && movie.posterPath.length > 0) {
        translatedImageUrls = [movie.posterPath];
    }

    // Not when the poster is hidden (CIN-14124): the operator is promised visitors see
    // a placeholder, and standing a backdrop in for the suppressed poster shows them an
    // image instead. The backdrop keeps its own switch, so it stays visible in its own
    // right — this only stops it substituting for the hidden type (CIN-14436).
    if (translatedImageUrls.length <= 0 && useBackdropAsFallback && !isImageTypeHidden(movie, 'poster')) {
        translatedImageUrls = getMovieBackdropImages(movie, {
            language,
            countryCode,
            usePosterAsFallback: false,
            imageOptions
        });
    }

    if (imageOptions?.useTmdbUrlPrefix !== false) {
        return translatedImageUrls.map((imageUrl) =>
            imageUrl.startsWith('http') ? imageUrl : (imageOptions?.resolution || TMDB_IMAGE_RESOLUTION.W780) + imageUrl
        );
    }

    return translatedImageUrls;
}

export function getMoviePosterBlurDataImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder = true } = options || {};

    const blurDataUrl = getMoviePosterImage(movie, {
        language,
        countryCode,
        useBackdropAsFallback,
        usePlaceholder,
        imageOptions: {
            resolution: TMDB_IMAGE_RESOLUTION.W92
        }
    });
    if (typeof blurDataUrl === 'string') {
        return blurDataUrl;
    }
    return blurDataUrl;
}

export function getMovieLogoImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined {
    const { language, countryCode, usePlaceholder = false, imageOptions } = options || {};

    const movieLogoImages = getMovieLogoImages(movie, { language, countryCode, imageOptions });

    if (movieLogoImages.length <= 0 && usePlaceholder) {
        return imageOptions?.placeholder;
    }
    return movieLogoImages?.[0];
}

export function getMovieLogoImages(movie?: MovieVersionDto | null, options?: MovieImageFilterOptions): string[] {
    const { language, countryCode, imageOptions } = options || {};

    // Logos are TMDB title-treatment images — pictures of the title text — so a logo may only be
    // shown when its words are the words the viewer reads: the viewer's locale first (country+
    // language, then language), then any other locale whose translated title is spelled exactly
    // the same, and only fall back to locale-less logos when explicitly requested via
    // includeImagesWithoutLocale.
    const logos: ImageDto[] = (movie?.images?.logos ?? (movie as MovieV2Dto)?.logos ?? []).slice();

    logos.sort(
        (imageA, imageB) =>
            // Admin-chosen primaries (CIN-14159) beat the rating (highest voteAverage first)
            Number(imageB.isPrimary ?? false) - Number(imageA.isPrimary ?? false) ||
            (imageB.voteAverage ?? 0.0) - (imageA.voteAverage ?? 0.0)
    );

    const logosForCountry = language ? logos.filter((image) => matchLocale(image, { language, countryCode })) : [];

    // for countries like AT or CH if no country-specific logo is found it's ok to use the DE logo
    const logosForLanguage = language
        ? logos.filter((image) =>
              matchLocale(image, {
                  language,
                  countryCode: DEFAULT_COUNTRY_CODE_BY_LANGUAGE[language],
                  includeDtoWithoutCountry: true,
                  includeDtoWithoutLocale: true
              })
          )
        : [];

    // Nothing in the viewer's own language: borrow the logo of a locale that happens to spell the
    // title identically (e.g. a `de` viewer reading "Hallo Welt" may use the `en` logo when the
    // English translation is "Hallo Welt", too).
    const logosForSameTitle =
        language && logosForCountry.length === 0 && logosForLanguage.length === 0
            ? getImagesForSameTitle(movie, logos, language, countryCode)
            : [];

    const nonLocaleLogos = logos.filter((image) => !image.iso6391);

    const selectedLogos = [
        ...logosForCountry,
        ...logosForLanguage,
        ...logosForSameTitle,
        ...(imageOptions?.includeImagesWithoutLocale ? nonLocaleLogos : [])
    ];

    const logoUrls = selectedLogos
        .map((image) => image.filePath ?? '')
        .filter((url) => url.length > 0)
        // the locale buckets can overlap (a logo matching both country and language), so de-duplicate
        .filter((url, index, urls) => urls.indexOf(url) === index);

    if (imageOptions?.useTmdbUrlPrefix !== false) {
        return logoUrls.map((imageUrl) => {
            if (imageUrl.startsWith('http')) {
                return imageUrl;
            }
            // TMDB only serves SVG logos at /original — sized paths like /w500 return an error — so
            // force ORIGINAL for .svg and use the requested resolution for raster (png/jpg) logos.
            const resolution = imageUrl.toLowerCase().endsWith('.svg')
                ? TMDB_IMAGE_RESOLUTION.ORIGINAL
                : imageOptions?.resolution || TMDB_IMAGE_RESOLUTION.W500;
            return resolution + imageUrl;
        });
    }

    return logoUrls;
}

// See getMoviePosterImage: `usePlaceholder` defaults to true, so the no-opt-out call shape
// always yields a string.
export function getMovieBackdropImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions & { usePlaceholder?: true }
): string;
export function getMovieBackdropImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined;
export function getMovieBackdropImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder = true, imageOptions } = options || {};

    const movieBackdropImages = getMovieBackdropImages(movie, {
        language,
        countryCode,
        usePosterAsFallback,
        imageOptions
    });

    if (movieBackdropImages.length <= 0 && usePlaceholder) {
        return imageOptions?.placeholder || MovieBackdropPlaceholder;
    }
    return movieBackdropImages?.[0];
}

export function getMovieBackdropImages(movie?: MovieVersionDto | null, options?: MovieImageFilterOptions): string[] {
    const { language, countryCode, usePosterAsFallback = false, imageOptions } = options || {};

    // Cinema customization: full CDN URL takes priority over TMDB images
    if (movie?.backdropPath?.startsWith('http')) {
        return [movie.backdropPath];
    }

    // slice(): never sort the (React Query cached) DTO array in place
    let translatedImages: ImageDto[] = (movie?.images?.backdrops ?? []).slice();

    translatedImages.sort(
        (imageA, imageB) =>
            // Admin-chosen primaries (CIN-14159) beat the rating (highest voteAverage first)
            Number(imageB.isPrimary ?? false) - Number(imageA.isPrimary ?? false) ||
            (imageB.voteAverage ?? 0.0) - (imageA.voteAverage ?? 0.0)
    );

    const translatedImagesForCountry = translatedImages.filter((image) =>
        matchLocale(image, {
            language,
            countryCode
        })
    );

    // for countries like AT or CH if no country-specific image is found it's ok to use the image for DE
    const translatedImagesForLanguage = translatedImages.filter((image) =>
        matchLocale(image, {
            language,
            countryCode: language ? DEFAULT_COUNTRY_CODE_BY_LANGUAGE[language] : null,
            includeDtoWithoutCountry: true,
            includeDtoWithoutLocale: true
        })
    );
    const translatedImagesForOriginalLanguage = translatedImages.filter((image) =>
        matchLocale(image, {
            language: movie?.originalLanguage as ISO_639_1_LOCALE,
            countryCode: null
        })
    );

    const nonLocaleImages = translatedImages.filter((image) => !image.iso6391);

    translatedImages = [
        // for backdrops we always prioritize images without text
        ...nonLocaleImages,
        ...translatedImagesForCountry,
        ...translatedImagesForLanguage,
        ...translatedImagesForOriginalLanguage
    ];

    let translatedImageUrls: string[] = translatedImages
        .map((image) => image.filePath ?? '')
        .filter((url) => url.length > 0);

    if (movie && translatedImageUrls.length <= 0 && movie.backdropPath && movie.backdropPath.length > 0) {
        translatedImageUrls = [movie.backdropPath];
    }

    // Mirror of the poster side: a hidden backdrop must not be replaced by a poster,
    // or the operator's "visitors see a placeholder" promise breaks (CIN-14436).
    if (translatedImageUrls.length <= 0 && usePosterAsFallback && !isImageTypeHidden(movie, 'backdrop')) {
        translatedImageUrls = getMoviePosterImages(movie, {
            language,
            countryCode,
            useBackdropAsFallback: false,
            imageOptions
        });
    }

    if (imageOptions?.useTmdbUrlPrefix !== false) {
        return translatedImageUrls.map((imageUrl) =>
            imageUrl.startsWith('http')
                ? imageUrl
                : (imageOptions?.resolution || TMDB_IMAGE_RESOLUTION.W1280) + imageUrl
        );
    }

    return translatedImageUrls;
}

export function getMovieBackdropBlurDataImage(
    movie?: MovieVersionDto | null,
    options?: MovieImageFilterOptions
): string | undefined {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder = true } = options || {};

    const blurDataUrl = getMovieBackdropImage(movie, {
        language,
        countryCode,
        usePosterAsFallback,
        usePlaceholder,
        imageOptions: {
            resolution: TMDB_IMAGE_RESOLUTION.W92
        }
    });
    if (typeof blurDataUrl === 'string') {
        return blurDataUrl;
    }
    return blurDataUrl;
}

export function getMovieTitle(
    movie?: MovieVersionDto | null,
    locale?: string,
    countryCode?: ISO_3166_1_LOCALE
): string | undefined {
    if (!movie) return undefined;

    // V1 movies have a `video` field (boolean) that V2 movies never include.
    // V1 needs client-side translation resolution from the translations array.
    const isV1 = isMovieV1(movie);
    if (isV1) {
        const translation = getTranslationData(movie, locale, 'title', countryCode);

        // If a translation is found but the title is empty and the originalLanguage matches, return the originalTitle.
        if (translation && !translation.data?.title && movie.originalLanguage === translation.iso6391) {
            return movie.originalTitle;
        }

        // Return the found title or fallback to the original titles.
        return translation?.data?.title || movie.originalTitle || movie.title;
    }

    // V2 movies have a pre-resolved, locale-aware title from the backend.
    return movie.title || movie.originalTitle;
}

export function getMovieOverview(movie?: MovieVersionDto | null, locale?: string): string | undefined {
    if (!movie) return undefined;
    // V1 movies need client-side translation resolution.
    if (isMovieV1(movie)) {
        return getTranslationData(movie, locale, 'overview')?.data?.overview || movie?.overview;
    }
    // V2 movies have a pre-resolved, locale-aware overview from the backend.
    return movie?.overview;
}

export function getMovieRuntime(movie?: MovieVersionDto | null, locale?: string): number | undefined {
    return getTranslationData(movie, locale, 'runtime')?.data?.runtime || movie?.runtime;
}

export function getMovieAgeRating(
    movie: MovieVersionDto | null | undefined,
    countryCode?: ISO_3166_1_LOCALE,
    releaseDateType: ReleaseDateType = ReleaseDateType.THEATRICAL
): string | undefined {
    const releaseDatesForLocale = movie?.releaseDates?.results?.find(
        (result) => result.iso31661?.toLowerCase() === countryCode?.toLowerCase()
    )?.releaseDates;

    const releaseDateForType = releaseDatesForLocale?.find(
        (releaseDate) => releaseDate.type === releaseDateType
    )?.certification;

    return (
        releaseDateForType ??
        releaseDatesForLocale?.find((releaseDate) => releaseDate.type === ReleaseDateType.THEATRICAL_LIMITED)
            ?.certification ??
        releaseDatesForLocale?.find((releaseDate) => releaseDate.type === ReleaseDateType.PREMIERE)?.certification ??
        releaseDatesForLocale?.find((releaseDate) => releaseDate.type === ReleaseDateType.PHYSICAL)?.certification ??
        releaseDatesForLocale?.find(
            (releaseDate) => releaseDate.certification != null && releaseDate.certification.length > 0
        )?.certification
    );
}

export function getMovieReleaseDate(movie?: MovieVersionDto | null, locale?: string | Locale): string | undefined {
    return getMovieReleaseDateForType(movie, ReleaseDateType.THEATRICAL, locale) || movie?.releaseDate;
}

export function getMovieReleaseDateForType(
    movie: MovieVersionDto | null | undefined,
    type: ReleaseDateType,
    locale?: string | Locale
): string | undefined {
    const countryCodeFromLocale =
        locale?.length === 2 && locale.toUpperCase() === locale ? locale : locale?.split('_')?.[1];
    const languageFromLocale =
        locale && locale !== countryCodeFromLocale
            ? (locale.split('_')[0].toLowerCase() as ISO_639_1_LOCALE)
            : undefined;

    const releaseDateForCountry = (countryCode?: string): string | undefined =>
        countryCode
            ? movie?.releaseDates?.results
                  ?.find((releaseDate) => releaseDate.iso31661?.toLowerCase() === countryCode.toLowerCase())
                  ?.releaseDates?.find((releaseDate) => releaseDate.type === type)?.releaseDate
            : undefined;

    // exact country first; if the locale has no country (e.g. bare 'de') or no dates exist for it,
    // fall back to the language's default country — same as poster/backdrop selection
    return (
        releaseDateForCountry(countryCodeFromLocale) ||
        releaseDateForCountry(languageFromLocale && DEFAULT_COUNTRY_CODE_BY_LANGUAGE[languageFromLocale])
    );
}

export function getMovieEarliestReleaseDate(movie?: MovieVersionDto | null): string | undefined {
    // earliest release date across all countries and release types (premiere, theatrical, digital, …),
    // falling back to the primary releaseDate when no per-country dates are known
    const allReleaseDates =
        movie?.releaseDates?.results
            ?.flatMap((country) => country.releaseDates ?? [])
            .map((releaseDate) => releaseDate.releaseDate)
            .filter((releaseDate): releaseDate is string => !!releaseDate && !isNaN(new Date(releaseDate).getTime())) ??
        [];

    const earliestReleaseDate = allReleaseDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];

    return earliestReleaseDate ?? movie?.releaseDate;
}

export function isMovieReleased(movie?: MovieVersionDto | null): boolean {
    // a movie without any known release date counts as released so that
    // catalogue entries with missing TMDB data are not locked out of ratings
    const earliestReleaseDate = getMovieEarliestReleaseDate(movie);
    return !earliestReleaseDate || new Date(earliestReleaseDate).getTime() <= Date.now();
}

export function getMovieReleaseYear(movie?: MovieVersionDto | null, locale?: string | Locale): string | undefined {
    const releaseDate: string | undefined = getMovieReleaseDate(movie, locale);
    return releaseDate && releaseDate.length >= 4 ? releaseDate?.substring(0, 4) : undefined;
}

export function getMovieHomepage(movie?: MovieVersionDto | null, locale?: string): string | undefined {
    return getTranslationData(movie, locale, 'homepage')?.data?.homepage || movie?.homepage;
}

export function getMovieStatus(movie?: MovieVersionDto | null): Status | undefined {
    return movie?.status;
}

export function getMovieProductionCountries(movie?: MovieVersionDto | null, locale?: string): string | undefined {
    return movie?.productionCountries
        ?.flatMap((value) => value.iso31661 && getCountryFromCode(value.iso31661, locale))
        .join(', ');
}

/**
 * Helper function to get a list of possible translations.
 * Let's say a movie has multiple iso31661 === 'en' translations.
 * But maybe a few of them do not have a title and others have no overview, runtime etc.
 * The we can just search for an alternative in most probably the same language 'en'.
 * For example 'en-US' has a title but 'en-GB' hasn't. Then let's use the US title instead.
 *
 * @param movie
 * @param locale
 */
function getTranslations(
    movie?: MovieVersionDto | null,
    locale?: string,
    countryCode?: ISO_3166_1_LOCALE
): TranslationDto[] | undefined {
    const languageOrLocale = locale ? locale : typeof navigator !== 'undefined' ? navigator.language : undefined;
    if (!languageOrLocale) {
        return [];
    }
    const country =
        countryCode ??
        (languageOrLocale.length === 5
            ? languageOrLocale.split('-')?.[1]
            : languageOrLocale.length === 2 && languageOrLocale.toUpperCase() === languageOrLocale
              ? languageOrLocale
              : undefined);
    const language = languageOrLocale.slice(0, 2);

    let translationsForCountryAndLanguage: TranslationDto[] = [];
    if (country && language) {
        translationsForCountryAndLanguage =
            movie?.translations?.translations?.filter(
                (translation) => translation.iso31661 === country && translation.iso6391 === language
            ) ?? [];
    }

    let translationsForCountry: TranslationDto[] = [];
    if (country) {
        translationsForCountry =
            movie?.translations?.translations?.filter((translation) => translation.iso31661 === country) ?? [];
    }

    let translationsForLanguage: TranslationDto[] = [];
    if (language) {
        translationsForLanguage =
            movie?.translations?.translations?.filter(
                (translation) => translation.iso6391?.toLowerCase() === language.toLowerCase()
            ) ?? [];
    }

    // Prio order: country and language, languag only, country only
    return [...translationsForCountryAndLanguage, ...translationsForLanguage, ...translationsForCountry];
}

export function getTranslationData(
    movie?: MovieVersionDto | null,
    locale?: string,
    requiredProperty?: keyof TranslationDataDto,
    countryCode?: ISO_3166_1_LOCALE
): TranslationDto | undefined {
    return getTranslations(movie, locale, countryCode)?.find(
        (translation) =>
            ((translation.iso6391?.length ?? 0) > 0 &&
                (movie?.originalLanguage?.length ?? 0) > 0 &&
                translation.iso6391 === movie?.originalLanguage) ||
            (requiredProperty ? (translation?.data?.[requiredProperty]?.toString()?.length ?? 0) > 0 : false)
    );
}

/**
 * GRETA accessibility payload of a movie. Both the v1 and the v2 movie endpoints deliver it under
 * `_embedded` (the HAL hydrator relocates object properties there), even though the v2 DTO also
 * declares it as a direct field — so always resolve it through this helper instead of reading
 * `movie.movieAccessibility`, which is `null` on the wire.
 */
/**
 * Structural carrier of the payload — a movie DTO (v1/v2) or an app's flattened movie view. `null`
 * is allowed on the field so views that resolve it eagerly (and store "no accessibility" as null)
 * fit without a cast.
 */
export type MovieWithAccessibility = {
    movieAccessibility?: MovieAccessibilityDto | null;
    _embedded?: { movieAccessibility?: MovieAccessibilityDto };
};

export function getMovieAccessibility(movie?: MovieWithAccessibility | null): MovieAccessibilityDto | null {
    // the direct field is read defensively, in case the hydrator ever stops relocating it
    return movie?.movieAccessibility ?? movie?._embedded?.movieAccessibility ?? null;
}

/**
 * Whether a movie has any GRETA accessibility features (closed captions, audio description or
 * sound amplification). Use this everywhere instead of repeating the individual flag checks —
 * e.g. to decide whether to render the GRETA accessibility dropdown/label.
 */
export function hasMovieAccessibilityFeatures(accessibility?: MovieAccessibilityDto | null): boolean {
    return !!(
        accessibility?.hasClosedCaption ||
        accessibility?.hasAudioDescription ||
        accessibility?.hasSoundAmplification
    );
}
